import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import useRazorpay from '../hooks/useRazorpay';
import { getRazorpayMethodConfig, getRazorpayUpiPrefill, isValidUpiId } from '../utils/razorpay';
import { useLanguage } from '../contexts/LanguageContext';
import './CourseDetailPage.css';

const CourseDetailPage = () => {
  const { t, language } = useLanguage();
  useRazorpay();
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, user, verifyToken, fetchEnrollments } = useAuth();

  const [course, setCourse] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [upiId, setUpiId] = useState('');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const fetchCourse = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.getPublicCourseDetails(courseId);
        
        // DEBUG LOG 1: Check the raw API response
        console.log("1. API Response:", response);

        if (response.success) {
          setCourse(response.data.course);
          setIsEnrolled(response.data.isEnrolled);
        } else {
          throw new Error(response.message || t('bhaktiYogaCourseDetail.errors.loadCourseDetails'));
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, isLoggedIn, t]);

  const handleEnrollFree = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    setIsProcessing(true);
    try {
      const response = await apiService.enrollInFreeCourse(courseId);
      if (response.success) {
        setIsEnrolled(true);
        await fetchEnrollments();
        navigate(`/courses/player/${courseId}`);
      } else {
        throw new Error(response.message || 'Enrollment failed.');
      }
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
    }
  };

  const handleSubscribeAndEnroll = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }

    if (!isValidUpiId(upiId)) {
      setError(t('bhaktiYogaCourseDetail.errors.invalidUpi'));
      return;
    }

    setIsProcessing(true);
    setError(null);

    const planId = course.subscriptionTier.toLowerCase();
    const plan = {
      id: planId,
      name: `${course.subscriptionTier} Plan`,
      price: planId === 'plus' ? 499 : 2499,
    };

    try {
      const razorpayOrderResponse = await apiService.createRazorpayOrder({
        amount: plan.price,
        entityId: plan.id,
        entityType: 'subscription',
        courseId: courseId,
      });

      if (!razorpayOrderResponse.success) {
        throw new Error(razorpayOrderResponse.message);
      }

      const razorpayOrder = razorpayOrderResponse.data;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: `Anandmaya - ${plan.name}`,
        description: `Access to ${plan.name} content`,
        order_id: razorpayOrder.id,
        handler: async function (response) {
          const verificationData = { ...response };
          await apiService.verifyRazorpayPayment(verificationData);
          await verifyToken();
          await fetchEnrollments();
          setIsEnrolled(true);
          navigate(`/courses/player/${courseId}`);
        },
        method: getRazorpayMethodConfig(),
        prefill: getRazorpayUpiPrefill({
          name: user.fullName,
          email: user.email,
        }, upiId),
        theme: { color: '#8B4513' },
        modal: {
          ondismiss: function () {
            setIsProcessing(false);
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      setError(err.message || t('bhaktiYogaCourseDetail.errors.unexpected'));
      setIsProcessing(false);
    }
  };

  const handleEnrollPaid = async () => {
    if (!isLoggedIn) {
      navigate('/login', { state: { from: window.location.pathname } });
      return;
    }
    setIsProcessing(true);
    try {
      const response = await apiService.enrollInPaidCourse(courseId);
      if (response.success) {
        setIsEnrolled(true);
        await fetchEnrollments();
        navigate(`/courses/player/${courseId}`);
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setError(err.message);
      setIsProcessing(false);
    }
  };


  const toTranslationKey = (value = '') => value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const getTranslatedTier = (tier) => (
    t(`bhaktiYogaPage.tiers.${toTranslationKey(tier)}`, {}, tier)
  );

  const getDynamicDetailFallback = (text = '', contentType = 'generic') => {
      // ... your existing translation fallback logic remains unchanged ...
      return text;
  };

  const translateDynamicText = (path, fallbackText, contentType) => {
    const translated = t(path, {}, fallbackText);
    if (translated !== fallbackText) return translated;
    return getDynamicDetailFallback(fallbackText, contentType);
  };

  const getTranslatedCourseTitle = (title) => translateDynamicText(`bhaktiYogaCourseDetail.courses.${toTranslationKey(title)}.title`, title, 'courseTitle');
  const getTranslatedCourseDescription = (description) => translateDynamicText(`bhaktiYogaCourseDetail.courses.${toTranslationKey(description)}.description`, description, 'courseDescription');
  const getTranslatedModuleTitle = (title) => translateDynamicText(`bhaktiYogaCourseDetail.modules.${toTranslationKey(title)}.title`, title, 'moduleTitle');
  const getTranslatedLessonTitle = (title) => translateDynamicText(`bhaktiYogaCourseDetail.lessons.${toTranslationKey(title)}.title`, title, 'lessonTitle');
  

  const renderEnrollmentButton = () => {
    if (isEnrolled) {
      return (
        <Link to={`/courses/player/${courseId}`} className="btn-enroll">
          {t('bhaktiYogaCourseDetail.actions.goToCourse')}
        </Link>
      );
    }

    if (!isLoggedIn) {
      return <Link to="/login" state={{ from: window.location.pathname }} className="btn-enroll">{t('bhaktiYogaCourseDetail.actions.loginToEnroll')}</Link>;
    }

    const hasSubscriptionAccess = user && (
      (course.subscriptionTier === 'Plus' && (user.subscription?.tier === 'plus' || user.subscription?.tier === 'premium')) ||
      (course.subscriptionTier === 'Premium' && user.subscription?.tier === 'premium')
    );

    if (hasSubscriptionAccess) {
      return (
        <button className="btn-enroll" onClick={handleEnrollPaid} disabled={isProcessing}>
          {isProcessing ? t('bhaktiYogaCourseDetail.actions.processing') : t('bhaktiYogaCourseDetail.actions.startCourse')}
        </button>
      );
    }

    switch (course?.subscriptionTier) {
      case 'Free':
        return (
          <button className="btn-enroll" onClick={handleEnrollFree} disabled={isProcessing}>
            {isProcessing ? t('bhaktiYogaCourseDetail.actions.enrolling') : t('bhaktiYogaCourseDetail.actions.enrollForFree')}
          </button>
        );
      case 'Plus':
      case 'Premium':
        return (
          <button className="btn-subscribe" onClick={handleSubscribeAndEnroll} disabled={true}>
            {/* disabled={isProcessing} --rm temporarily */}
            {isProcessing
              ? t('bhaktiYogaCourseDetail.actions.processing')
              : t('bhaktiYogaCourseDetail.actions.subscribeToTier', { tier: getTranslatedTier(course.subscriptionTier) })}
          </button>
        );
      default:
        return null;
    }
  };

  if (isLoading) { return <div className="loading-spinner">{t('bhaktiYogaCourseDetail.loadingCourse')}</div>; }
  if (error && !isProcessing) { return <div className="error-message">{t('bhaktiYogaCourseDetail.errorPrefix')}: {error}</div>; }
  if (!course) { return <div className="error-message">{t('bhaktiYogaCourseDetail.courseNotFound')}</div>; }

  // DEBUG LOG 2: Check state right before rendering
  console.log("2. Render State -> isEnrolled:", isEnrolled);

  return (
    <div className="course-detail-page">
      <div className="course-hero" style={{ backgroundImage: `url(${course.thumbnail})` }}>
        <div className="hero-overlay">
          <p>{course.instructor}</p>
          <span className={`tier-badge tier-${course.subscriptionTier?.toLowerCase()}`}>{getTranslatedTier(course.subscriptionTier)}</span>
        </div>
      </div>

      <div className="container course-content-layout">
        <main className="course-main-content">
          <h1>{getTranslatedCourseTitle(course.title)}</h1>

          <h2>{t('bhaktiYogaCourseDetail.sections.aboutThisCourse')}</h2>
          <p>{getTranslatedCourseDescription(course.description)}</p>

          <h2>{t('bhaktiYogaCourseDetail.sections.curriculum')}</h2>
          <div className="curriculum-accordion">
            {course.modules && course.modules.sort((a, b) => a.order - b.order).map((module) => {
              
              // --- THE FIX & DEEP DEBUGGING ---
              // Determine if the module itself is a paid module
              const isPaidModule = module.subscriptionTier && module.subscriptionTier !== 'Free';
              
              // Show the lock ONLY IF it's a paid module AND the user is NOT enrolled
              const showLock = isPaidModule && !isEnrolled;

              // DEBUG LOG 3: Print out the exact evaluation for every single module
              console.log(`--- Evaluating Module: ${module.title} ---`);
              console.log("   Module Tier:", module.subscriptionTier);
              console.log("   isPaidModule:", isPaidModule);
              console.log("   Global isEnrolled:", isEnrolled);
              console.log("   RESULT -> showLock:", showLock);
              // --------------------------------
              
              return (
                <div key={module._id} className="module-section">
                  <h3 className="module-title">
                    {module.order}. {getTranslatedModuleTitle(module.title)}
                    {showLock && (
                      <span className={`tier-badge-small tier-${module.subscriptionTier.toLowerCase()}`}>
                        <i className="fas fa-lock"></i> {getTranslatedTier(module.subscriptionTier)}
                      </span>
                    )}
                  </h3>
                  <ul className="lesson-list">
                    {module.lessons && module.lessons.sort((a, b) => a.order - b.order).map((lesson) => (
                      <li key={lesson._id}>
                        <i className={`fas ${lesson.type === 'Video' ? 'fa-play-circle' : 'fa-file-alt'}`}></i>
                        {getTranslatedLessonTitle(lesson.title)}
                        {lesson.duration && <span className="lesson-duration">{lesson.duration} {t('bhaktiYogaCourseDetail.minutesShort')}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </main>
        
        <aside className="course-sidebar">
          <div className="enroll-box">
            <img src={course.thumbnail} alt={course.title} className="sidebar-thumbnail" />
            <div className="enroll-box-content">
              {error && <p className="sidebar-error">{error}</p>}
              
              {/* Only show UPI input if user needs to subscribe and hasn't yet */}
              {isLoggedIn && !isEnrolled && (course?.subscriptionTier === 'Plus' || course?.subscriptionTier === 'Premium') && (
                <div> </div>
                /*
                <div className="upi-id-input-wrapper">
                  <label htmlFor="course-upi-id">{t('bhaktiYogaCourseDetail.form.upiIdLabel')}</label>
                  <input
                    id="course-upi-id"
                    type="text"
                    placeholder={t('bhaktiYogaCourseDetail.form.upiIdPlaceholder')}
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                  />
                </div>
                */
              )}
              {renderEnrollmentButton()}
              <p className="sidebar-note">{t('bhaktiYogaCourseDetail.sidebar.fullAccessNote')}</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default CourseDetailPage;